import { useState } from 'react';
import {
  formatPrice,
  calculateDaysDiff,
} from '../../utilities/FormatFunctions';
import {
  AreaIcon,
  BedroomsIcon,
  BathroomsIcon,
  GarageIcon,
  FloorIcon,
  YardsIcon,
  CityIcon,
  LocationIcon,
  KitchenIcon,
  HouseIcon,
  StateIcon,
} from '../../utilities/icons/RealEstatesIcons';
import { useNavigate } from 'react-router-dom';

export default function RealEstatesDetail({ selectedRealEstate }) {
  const { imageUrls } = selectedRealEstate;
  const [mainImage, setMainImage] = useState(imageUrls[0]);
  const differenceInDays = calculateDaysDiff(selectedRealEstate.createdAt);
  const navigate = useNavigate();

  const getRealEstateTypeName = () => {
    if (selectedRealEstate.realEstateType?.name) {
      return selectedRealEstate.realEstateType.name;
    }

    if (selectedRealEstate.realEstateTypeName) {
      return selectedRealEstate.realEstateTypeName;
    }

    if (typeof selectedRealEstate.realEstateType === 'string') {
      return selectedRealEstate.realEstateType;
    }

    return '';
  };

  const isLot = getRealEstateTypeName().toLowerCase().includes('lote');

  const specifications = isLot
    ? [
        {
          icon: <AreaIcon className="text-customBlue" />,
          label: 'Area',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.area} m²</p>
          ),
        },
        {
          icon: <CityIcon className="text-customBlue" />,
          label: 'Ciudad',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.city}</p>
          ),
        },
        {
          icon: <LocationIcon className="text-customBlue" />,
          label: 'Sector',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.sector}</p>
          ),
        },
        {
          icon: <StateIcon className="text-customBlue" />,
          label: 'Estado',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.state}</p>
          ),
        },
      ]
    : [
        {
          icon: <AreaIcon className="text-customBlue" />,
          label: 'Area',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.area} m²</p>
          ),
        },
        {
          icon: <BedroomsIcon className="text-customBlue" />,
          label: 'Cuartos',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.bedrooms}</p>
          ),
        },
        {
          icon: <BathroomsIcon className="text-customBlue" />,
          label: 'Baños',
          value: (
            <p className="font-medium text-sm">
              {selectedRealEstate.bathrooms}
            </p>
          ),
        },
        {
          icon: <GarageIcon className="text-customBlue" />,
          label: 'Garage',
          value: (
            <p className="font-medium text-sm">
              {selectedRealEstate.garage ? 'Si' : 'No'}
            </p>
          ),
        },
        {
          icon: <FloorIcon className="text-customBlue" />,
          label: 'Piso',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.floor}</p>
          ),
        },
        {
          icon: <YardsIcon className="text-customBlue" />,
          label: 'Patios',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.yards}</p>
          ),
        },
        {
          icon: <CityIcon className="text-customBlue" />,
          label: 'Ciudad',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.city}</p>
          ),
        },
        {
          icon: <LocationIcon className="text-customBlue" />,
          label: 'Sector',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.sector}</p>
          ),
        },
        {
          icon: <KitchenIcon className="text-customBlue" />,
          label: 'Cocinas',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.kitchens}</p>
          ),
        },
        {
          icon: <HouseIcon className="text-customBlue" />,
          label: 'Balcón',
          value: (
            <p className="font-medium text-sm">
              {selectedRealEstate.balcony ? 'Si' : 'No'}
            </p>
          ),
        },
        {
          icon: <StateIcon className="text-customBlue" />,
          label: 'Estado',
          value: (
            <p className="font-medium text-sm">{selectedRealEstate.state}</p>
          ),
        },
      ];

  const handleClick = () => {
    navigate('/ventas', { state: { scrollToContent: true } });
  };

  return (
    <main className="min-h-screen px-4 pt-20 md:pt-20 lg:pt-32">
      <div className="w-full sm:mt-26 lg:grid lg:grid-cols-2 gap-8 items-center lg:items-start">
        {/* Columna 1: Galería de imágenes */}
        <div className="mx-2 mt-20 lg:ml-4 lg:mt-8">
          <div className="relative mb-4">
            <div className="absolute top-4 right-10 flex items-center space-x-2">
              {differenceInDays <= 15 && (
                <span className="bg-amber-300 border font-heading border-gray-600 tracking-wide text-black font-medium shadow-custom-left-light text-[10px] md:text-xs px-2 py-0.5 md:px-3 md:py-1 rounded-full">
                  ¡Nuevo!
                </span>
              )}
              <span
                className={`border font-heading border-gray-700 tracking-wide text-black font-medium shadow-custom-left-light text-[10px] md:text-xs px-2 py-0.5 md:px-3 md:py-1 rounded-full
                                  ${
                                    selectedRealEstate.businessType !== 'Venta'
                                      ? 'bg-indigo-300'
                                      : 'bg-emerald-300'
                                  }`}
              >
                {selectedRealEstate.businessType !== 'Venta'
                  ? 'En arriendo'
                  : 'En venta'}
              </span>
            </div>
            <img
              src={mainImage}
              alt="Vista frontal de una casa moderna"
              className="w-full h-[550px] object-cover rounded-2xl"
            />
            <div className="absolute bottom-0 left-0 p-8 bg-gradient-to-t from-black/70 to-transparent w-full rounded-b-2xl">
              <h1 className="text-4xl font-bold text-white">
                {selectedRealEstate.name}
              </h1>
            </div>
          </div>
          {/* Thumbnails con scroll lateral si hay más de 5 imágenes */}
          <div
            className={`flex gap-4 overflow-x-auto pb-2 ${
              imageUrls.length > 5
                ? 'scrollbar-thin scrollbar-thumb-gray-300'
                : ''
            }`}
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {imageUrls.map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`Vista ${i + 1}`}
                onClick={() => setMainImage(img)}
                className={`rounded-lg h-32 min-w-[120px] object-cover cursor-pointer border-2 transition-colors ${
                  mainImage === img
                    ? 'border-emerald-500'
                    : 'border-transparent'
                }`}
              />
            ))}
          </div>
        </div>
        {/* Columna 2: Info, descripción, especificaciones y botón */}
        <div className="space-y-8 mx-2 xs:mt-4 mb-4 lg:mr-4 mt-8">
          <div className="bg-gray-50 p-8 rounded-2xl border border-gray-300">
            <div className="flex justify-between items-start">
              <div className="text-3xl font-bold inline-flex items-baseline">
                <span className="text-gray-800">Precio:&nbsp;</span>
                <span className="text-customBlue">
                  ${formatPrice(selectedRealEstate.price)}
                </span>
                {selectedRealEstate.businessType === 'Arriendo' && (
                  <span className="text-base font-normal text-gray-500 leading-none">
                    /mes
                  </span>
                )}
              </div>
            </div>
            <hr className="my-6 border-gray-300" />
            <div>
              <h2 className="text-xl font-bold mb-2">Descripción</h2>
              <p className="text-gray-600 leading-relaxed text-sm">
                {selectedRealEstate.description}
              </p>
            </div>
          </div>
          {/* Especificaciones */}
          <div className="bg-gray-50 p-8 rounded-2xl border border-gray-300">
            <h2 className="text-xl font-bold mb-4">Especificaciones</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
              {specifications.map((spec, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <span className="material-symbols-outlined text-2xl text-emerald-500">
                    {spec.icon}
                  </span>
                  <div>
                    <p className="font-medium">{spec.label}</p>
                    <p className="text-gray-600 text-sm">{spec.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className=" flex justify-center mt-10 pb-4">
            <button
              onClick={handleClick}
              className=" relative transition-all ease-in-out duration-300 shadow-[0px_10px_20px_rgba(0,0,0,0.2)] py-2 px-14 bg-[rgb(0,107,179)] rounded-full flex items-center justify-center cursor-pointer text-white gap-2 font-bold border-3 border-[#ffffff4d] outline-none overflow-hidden text-[15px] hover:scale-105 hover:border-[#fff9] before:absolute before:top-0 before:left-[-100px] before:w-[100px] before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/80 before:to-transparent before:opacity-60 hover:before:animate-shine"
            >
              <svg
                className="h-8 w-8 mr-1"
                version="1.1"
                id="Layer_1"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                viewBox="0 0 503.467 503.467"
                xmlSpace="preserve"
                fill="#000000"
                transform="rotate(0)matrix(-1, 0, 0, 1, 0, 0)"
              >
                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                <g
                  id="SVGRepo_tracerCarrier"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                  <g transform="translate(1 1)">
                    <path
                      style={{ fill: '#FF7474' }}
                      d="M361.667,199.533c0-89.6-72.533-162.133-162.133-162.133S37.4,109.933,37.4,199.533 s72.533,162.133,162.133,162.133S361.667,289.133,361.667,199.533 M395.8,199.533c0,8.533-0.853,17.067-1.707,25.6 c-4.267,32.427-16.213,62.293-34.133,87.893c-12.8,18.773-29.013,34.987-47.787,47.787c-31.573,22.187-70.827,35.84-112.64,35.84 C91.16,396.653,3.267,308.76,3.267,200.387S91.16,4.12,199.533,4.12c82.773,0,153.6,51.2,182.613,123.733 C390.68,149.187,395.8,173.933,395.8,199.533"
                    ></path>
                    <path
                      style={{ fill: '#AAB1BA' }}
                      d="M428.227,380.44l-47.787,47.787l-0.853,0.853l-66.56-66.56l-0.853-1.707 c18.773-12.8,34.987-29.013,47.787-47.787l1.707,0.853L428.227,380.44z"
                    ></path>
                    <path
                      style={{ fill: '#80D6FA' }}
                      d="M487.96,440.173c13.653,13.653,13.653,34.987,0,48.64s-34.987,13.653-48.64,0l-59.733-59.733 l0.853-0.853l47.787-47.787l0,0L487.96,440.173z"
                    ></path>
                    <path
                      style={{ fill: '#FFD0A1' }}
                      d="M481.133,182.467v93.867h-51.2v-51.2h-33.28h-2.56c0.853-8.533,1.707-17.067,1.707-25.6 c0-25.6-5.12-50.347-13.653-72.533l1.707-0.853l20.48-20.48L481.133,182.467z"
                    ></path>
                    <path
                      style={{ fill: '#ECF4F7' }}
                      d="M199.533,37.4c89.6,0,162.133,72.533,162.133,162.133s-72.533,162.133-162.133,162.133 S37.4,289.133,37.4,199.533S109.933,37.4,199.533,37.4L199.533,37.4z M276.333,182.467l-76.8-76.8l-76.8,76.8v93.867h59.733v-51.2 H216.6v51.2h59.733V182.467z"
                    ></path>
                    <polygon
                      style={{ fill: '#FFD0A1' }}
                      points="276.333,182.467 276.333,276.333 216.6,276.333 216.6,225.133 182.467,225.133 182.467,276.333 122.733,276.333 122.733,182.467 199.533,105.667"
                    ></polygon>
                  </g>
                  <path
                    style={{ fill: '#51565F' }}
                    d="M465.067,503.467c-10.24,0-19.627-4.267-27.307-11.093L337.067,390.827 c-1.707-1.707-1.707-4.267,0-5.973c1.707-1.707,4.267-1.707,5.973,0L443.733,486.4c5.973,5.973,12.8,8.533,21.333,8.533l0,0 c7.68,0,15.36-3.413,21.333-8.533c5.973-5.973,8.533-12.8,8.533-21.333c0-8.533-3.413-15.36-8.533-21.333l-57.173-57.173 l-21.333,21.333c-1.707,1.707-4.267,1.707-5.973,0c-1.707-1.707-1.707-4.267,0-5.973l23.893-23.893 c0.853-0.853,1.707-0.853,3.413-0.853l0,0c0.853,0,2.56,0.853,3.413,0.853l59.733,59.733c7.68,6.827,11.093,17.067,11.093,27.307 c0,10.24-4.267,19.627-11.093,27.307C484.693,499.2,475.307,503.467,465.067,503.467L465.067,503.467z M200.533,401.067 C89.6,401.067,0,311.467,0,200.533S89.6,0,200.533,0s200.533,89.6,200.533,200.533S311.467,401.067,200.533,401.067z M200.533,8.533 c-105.813,0-192,86.187-192,192s86.187,192,192,192s192-86.187,192-192S306.347,8.533,200.533,8.533z M200.533,366.933 c-92.16,0-166.4-74.24-166.4-166.4s74.24-166.4,166.4-166.4c92.16,0,166.4,74.24,166.4,166.4S292.693,366.933,200.533,366.933z M200.533,42.667c-87.04,0-157.867,70.827-157.867,157.867S113.493,358.4,200.533,358.4S358.4,287.573,358.4,200.533 S287.573,42.667,200.533,42.667z M406.187,361.813c-0.853,0-2.56,0-3.413-0.853l-17.92-17.92C384,342.186,384,341.333,384,339.627 s0-2.56,0.853-3.413c1.707-1.707,4.267-1.707,5.973,0l17.92,17.92c0.853,0.853,0.853,1.707,0.853,3.413s0,2.56-0.853,3.413 C407.893,361.813,407.04,361.813,406.187,361.813z M482.133,281.6h-51.2c-2.56,0-4.267-1.707-4.267-4.267v-51.2 c0-2.56,1.707-4.267,4.267-4.267h28.16v-38.4c0-1.707,1.707-3.413,2.56-4.267c1.707-0.853,4.267-0.853,5.12,0l29.013,29.013 c0.853,0.853,0.853,1.707,0.853,2.56v66.56C486.4,280.747,484.693,281.6,482.133,281.6L482.133,281.6z M435.2,273.067h42.667 v-60.16l-24.747-24.747v34.987c0,2.56-1.707-4.267-4.267-4.267h-38.4V273.067z M268.8,281.6h-59.733c-2.56,0-4.267-1.707-4.267-4.267 v-46.933h-25.6v46.933c0,2.56,1.707-4.267,4.267-4.267H122.8c-2.56,0-4.267-1.707-4.267-4.267V182.467c0-1.707,0.853-2.56,1.707-3.413 l76.8-76.8c1.707-1.707,4.267-1.707,5.973,0l76.8,76.8c0.853,0.853,1.707,1.707,1.707,3.413v93.867 C272.213,279.893,270.507,281.6,268.8,281.6z M213.067,273.067h51.2v-89.6l-72.533-72.533l-72.533,72.533v89.6h51.2v-46.933 c0-2.56,1.707-4.267,4.267-4.267h33.28c2.56,0,4.267,1.707,4.267,4.267V273.067z"
                  ></path>
                </g>
              </svg>
              Ver todos los inmuebles
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
